import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  ShieldAlert, 
  Calendar, 
  Hash, 
  Cpu, 
  ArrowLeft,
  CheckCircle2,
  Clock,
  QrCode
} from 'lucide-react';
import { motion } from 'framer-motion';

interface PublicVerificationDetails {
  id: string;
  idType: string;
  status: 'verified' | 'failed' | 'pending';
  proof: {
    idHash: string;
    confidence: number;
  };
  qrMatch?: 'match' | 'mismatch' | 'not_found' | null;
  blockchain: {
    txHash: string;
    confirmed: boolean;
  } | null;
  timestamps: {
    created: string;
    completed: string;
  };
}

export default function PublicVerifyDetails() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<PublicVerificationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await fetch(`/api/verifications/public/${id}`);
        const json = await response.json();
        
        if (!response.ok) {
          throw new Error(json.error || 'Failed to fetch verification details');
        }
        
        setData(json.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070b14] flex flex-col items-center justify-center text-white">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="mb-4"
        >
          <Clock className="w-12 h-12 text-blue-500 opacity-50" />
        </motion.div>
        <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Authenticating with Protocol...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#070b14] flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full bg-red-500/5 border border-red-500/10 p-8 rounded-3xl text-center">
          <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-white mb-2">Verification Not Found</h2>
          <p className="text-slate-400 mb-8">The requested verification ID does not exist or has expired.</p>
          <Link to="/verify" className="inline-flex items-center gap-2 text-white bg-slate-800 hover:bg-slate-700 px-6 py-3 rounded-xl font-bold transition-all">
            <ArrowLeft className="w-4 h-4" />
            Back to Search
          </Link>
        </div>
      </div>
    );
  }

  const isVerified = data.status === 'verified';

  return (
    <div className="min-h-screen bg-[#070b14] pt-32 pb-24 px-4 overflow-hidden relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-[600px] bg-blue-600/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-3xl mx-auto relative z-10">
        <Link to="/verify" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-12 transition-colors group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-bold uppercase tracking-widest">Back to Trust Portal</span>
        </Link>

        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`relative overflow-hidden p-8 md:p-12 rounded-[2.5rem] border ${
            isVerified ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'
          } mb-12 shadow-2xl`}
        >
          {/* Status Icon & Label */}
          <div className="flex flex-col md:flex-row items-center gap-8 mb-10">
            <div className={`p-6 rounded-3xl ${isVerified ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
              {isVerified ? <ShieldCheck className="w-16 h-16" /> : <ShieldAlert className="w-16 h-16" />}
            </div>
            <div className="text-center md:text-left">
              <span className={`text-xs font-black uppercase tracking-[0.3em] mb-2 block ${isVerified ? 'text-emerald-400' : 'text-red-400'}`}>
                {isVerified ? 'Report Legitimate' : 'Verification Invalid'}
              </span>
              <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">
                {isVerified ? 'Verified Authenticity' : 'Failed Verification'}
              </h1>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-950/40 rounded-3xl p-8 border border-slate-800/30">
            <div className="space-y-1">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Verification ID</span>
              <p className="text-white font-mono text-sm truncate">{data.id}</p>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">ID Type</span>
              <p className="text-white font-bold uppercase tracking-wide">{data.idType.replace(/_/g, ' ')}</p>
            </div>
          </div>
        </motion.div>

        {/* Detailed Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-900/40 backdrop-blur-md border border-slate-800/40 p-8 rounded-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400">
                <Hash className="w-5 h-5" />
              </div>
              <h3 className="text-white font-black text-sm uppercase tracking-widest">Cryptographic Proof</h3>
            </div>
            <div className="space-y-4">
              <div>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">ID Number Hash (SHA-256)</span>
                <p className="text-slate-200 font-mono text-[10px] bg-slate-950/80 p-3 rounded-xl break-all leading-relaxed">
                  {data.proof.idHash}
                </p>
              </div>
              <div className="flex justify-between items-center py-3 border-t border-slate-800/50">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Confidence Score</span>
                <span className="text-white font-black font-mono">{(data.proof.confidence * 100).toFixed(2)}%</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-900/40 backdrop-blur-md border border-slate-800/40 p-8 rounded-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-indigo-500/10 text-indigo-400">
                <Calendar className="w-5 h-5" />
              </div>
              <h3 className="text-white font-black text-sm uppercase tracking-widest">Audit Timestamps</h3>
            </div>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Completed On</span>
                  <p className="text-white font-bold">{new Date(data.timestamps.completed).toLocaleString()}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 opacity-60">
                <Clock className="w-5 h-5 text-slate-500" />
                <div>
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">Initiated On</span>
                  <p className="text-slate-300 font-bold">{new Date(data.timestamps.created).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* QR Verification Status */}
          {data.qrMatch && (data.qrMatch === 'match' || data.qrMatch === 'mismatch') && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-slate-900/40 backdrop-blur-md border border-slate-800/40 p-8 rounded-3xl md:col-span-2"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className={`p-2 rounded-lg ${
                  data.qrMatch === 'match' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                }`}>
                  <QrCode className="w-5 h-5" />
                </div>
                <h3 className="text-white font-black text-sm uppercase tracking-widest">Digital Integrity</h3>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                   <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">QR Cross-Check</span>
                   <p className={`font-bold font-mono text-sm ${
                     data.qrMatch === 'match' ? 'text-emerald-400' : 'text-red-400'
                   }`}>
                     {data.qrMatch === 'match' ? 'CRYPTOGRAPHIC MATCH' : 'DATA MISMATCH DETECTED'}
                   </p>
                </div>
                {data.qrMatch === 'match' ? 
                  <CheckCircle2 className="w-6 h-6 text-emerald-500/50" /> : 
                  <ShieldAlert className="w-6 h-6 text-red-500/50" />
                }
              </div>
            </motion.div>
          )}
        </div>

        {/* Cryptographic Assurance Section (Replacement for Blockchain) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-blue-500/5 to-slate-500/5 border border-slate-800/50 p-8 rounded-[2.5rem] text-center"
        >
          <Cpu className="w-10 h-10 text-blue-500/40 mx-auto mb-4" />
          <h3 className="text-white font-black text-lg mb-2">Cryptographic Hash Assurance</h3>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            This record is protected by a one-way SHA-256 cryptographic hash, ensuring the integrity and origin of the verification data without exposing sensitive information.
          </p>
        </motion.div>

        {/* Verification Summary */}
        <div className="mt-16 text-center space-y-6">
          <p className="text-slate-500 text-sm max-w-lg mx-auto italic">
            This report serves as a digital assurance that the specified identity document was processed by Agos Intelligence and met the security protocols at the time of verification.
          </p>
          <div className="pt-8 flex justify-center">
            <Link to="/" className="text-blue-400 hover:text-blue-300 font-black text-xs uppercase tracking-[0.3em] flex items-center gap-3 transition-colors">
              <span className="w-12 h-px bg-blue-500/30" />
              Return to Agos Identity
              <span className="w-12 h-px bg-blue-500/30" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
